import React, { useState } from 'react';
import { Location } from 'history';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import {
  SearchParamsPetCounts,
  SearchParamsPetType,
} from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Flex } from '@rover/kibble/core';
import Banner from '@rover/kibble/official-patterns/Banner';
import IntroVideoProfilePrompt from '@rover/react-lib/src/pages/intro-video/components/IntroVideoProfilePrompt';
import {
  IntroVideoEntrypoint,
  introVideoManageUrl,
} from '@rover/react-lib/src/pages/intro-video/sharedConstants';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';
import {
  type PriceTransparencyLevel,
  PriceTransparencyLevels,
} from '@rover/types/src/PriceTransparencyLevel';

import { IntroVideoProfilePromptPressed } from '../analyticsEvents';
import { useRevampProfileContext } from '../hooks/useRevampProfileContext';

import AllServicesSection from './AllServicesSection/AllServicesSection';
import HighlightsSection from './HighlightsSection/HighlightsSection';
import PetTypePreferencesSection from './PetTypePreferencesSection/PetTypePreferencesSection';
import ReportProfileSection from './ReportProfileSection/ReportProfileSection';
import RevampAvailabilityCalendar from './RevampAvailabilityCalendar/RevampAvailabilityCalendar';
import RevampReviewsAndRatings from './RevampReviewsAndRatings/RevampReviewsAndRatings';
import RevampWhereYourPetWillStay from './RevampWhereYourPetWillStay/RevampWhereYourPetWillStay';
import WhatSitterWantsToKnowSection from './WhatSitterWantsToKnowSection/WhatSitterWantsToKnowSection';
import RevampPetCareExperienceSection from './RevampPetCareExperienceSection';
import RevampStickySidebar from './RevampStickySidebar';

type Props = {
  locale: Locale;
  selectedPetType: SearchParamsPetType;
  petCounts: SearchParamsPetCounts;
  selectedDate: Date;
  selectedEndDate?: Date;
  hasDateRange?: boolean;
  isRequoting?: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  isShowingPriceInCurrency?: boolean;
  bookingFeePercentage?: string;
  navigationIsEU?: boolean;
  location: Location;
  opk: string;
  sitterAvatar: string;
  isApproved: boolean;
};

const RevampPageContent = ({
  locale,
  selectedPetType,
  petCounts,
  selectedDate,
  selectedEndDate,
  hasDateRange,
  isRequoting,
  priceTransparencyLevel,
  isShowingPriceInCurrency,
  bookingFeePercentage,
  navigationIsEU,
  location,
  opk,
  sitterAvatar,
  isApproved,
}: Props): JSX.Element => {
  const { sitterData } = useRevampProfileContext();
  const currentUser = useCurrentUser();
  const [success, setSuccess] = useState<string>();

  const showSuccess = (successDescription: string): void => {
    setSuccess(successDescription);
  };

  const dismissSuccess = (): void => {
    setSuccess(undefined);
  };
  return (
    <Flex
      data-testid="revamp-page-content"
      flexDirection={['column', 'column', 'row']}
      sx={{ gap: ['0x', '0x', '6x'] }}
    >
      {/* Sticky sidebar — rendered first in the DOM so screen readers and keyboard
          tab order reach the services CTA right after the gallery, before the main
          content. The order prop keeps it visually on the right on desktop. */}
      <RevampStickySidebar
        priceTransparencyLevel={priceTransparencyLevel || PriceTransparencyLevels.none}
        locale={locale}
        petCount={petCounts}
        petType={selectedPetType}
        bookingFeePercentage={bookingFeePercentage || ''}
        isApproved={isApproved}
        selectedDate={selectedDate}
        selectedEndDate={selectedEndDate}
        hasDateRange={hasDateRange}
      />

      {/* Main content column. It owns a stacking context so the z-indexes its
          sections set — the availability calendar's month captions among them —
          stay under the sidebar instead of painting over the dates dropdown. */}
      <Flex
        flex="1"
        flexDirection="column"
        width={['100%', '100%', 'initial']}
        order={[1, 1, 0]}
        position="relative"
        zIndex="0"
      >
        {sitterData.introVideoPrompt && (
          <IntroVideoProfilePrompt
            mt="6x"
            href={introVideoManageUrl(IntroVideoEntrypoint.ProfilePrompt)}
            onPress={() => emitAnalyticsEvent(new IntroVideoProfilePromptPressed())}
          />
        )}
        <HighlightsSection />
        <RevampPetCareExperienceSection />
        <RevampReviewsAndRatings opk={opk} sitterAvatar={sitterAvatar} />
        <RevampAvailabilityCalendar
          locale={locale}
          selectedDate={selectedDate}
          isEU={navigationIsEU}
        />
        <RevampWhereYourPetWillStay />
        <Box webAs="dl" m="0x">
          <PetTypePreferencesSection />
          <WhatSitterWantsToKnowSection />
          <AllServicesSection
            selectedPetType={selectedPetType}
            petCounts={petCounts}
            isRequoting={isRequoting}
            priceTransparencyLevel={priceTransparencyLevel}
            isShowingPriceInCurrency={isShowingPriceInCurrency}
            bookingFeePercentage={bookingFeePercentage}
          />
        </Box>
        {currentUser.user?.opk !== sitterData.opk && (
          <Flex py="10x" width="100%">
            <ReportProfileSection
              isLoggedIn={currentUser.isAuthenticated}
              location={location}
              onSuccess={showSuccess}
            />
          </Flex>
        )}
      </Flex>
      {success && (
        <Flex
          zIndex="99999"
          position="fixed"
          left={['none', 'none', '40px']}
          textAlign="center"
          justifyContent="center"
        >
          <Banner
            severity="success"
            hideIcon
            onClose={dismissSuccess}
            containerProps={{ mb: '0x' }}
          >
            {success}
          </Banner>
        </Flex>
      )}
    </Flex>
  );
};

export default RevampPageContent;
